import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleContents } from "@/mocks/data";
import PageMeta, { getSiteUrl } from "@/components/feature/PageMeta";

const siteUrl = getSiteUrl();

const articleHeroImages: Record<string, string> = {
  "hipobuy-spreadsheet-vs-excel": "https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20of%20two%20laptops%20on%20minimalist%20white%20desk%20showing%20different%20spreadsheet%20programs%2C%20modern%20office%20setup%2C%20bright%20studio%20lighting%2C%20clean%20tech%20editorial%20photography&width=1200&height=600&seq=art-hero-v2-01&orientation=landscape",
  "comment-utiliser-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Step%20by%20step%20tutorial%20workspace%20with%20laptop%20notebook%20and%20highlighters%20on%20light%20wood%20desk%2C%20organized%20learning%20environment%2C%20soft%20morning%20light%2C%20educational%20photography&width=1200&height=600&seq=art-hero-v2-02&orientation=landscape",
  "alternatives-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Multiple%20tablets%20and%20phones%20displaying%20various%20app%20interfaces%20on%20concrete%20table%2C%20tech%20ecosystem%20comparison%2C%20bright%20overhead%20lighting%2C%20digital%20tools%20flat%20lay&width=1200&height=600&seq=art-hero-v2-03&orientation=landscape",
  "avantages-limites-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Professional%20weighing%20pros%20and%20cons%20with%20laptop%20showing%20analytics%20at%20sunlit%20desk%2C%20decision%20making%20concept%2C%20balanced%20composition%2C%20warm%20natural%20light%2C%20editorial%20style&width=1200&height=600&seq=art-hero-v2-04&orientation=landscape",
  "guide-debutant-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Cozy%20home%20office%20with%20beginner%20tutorial%20on%20laptop%20screen%20steaming%20coffee%20mug%20and%20plant%2C%20welcoming%20learning%20atmosphere%2C%20warm%20sunlight%2C%20accessible%20tech%20photography&width=1200&height=600&seq=art-hero-v2-05&orientation=landscape",
  "astuces-avancees-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Advanced%20data%20visualization%20dashboard%20on%20ultrawide%20curved%20monitor%20in%20dark%20modern%20office%2C%20professional%20analyst%20setup%2C%20ambient%20lighting%2C%20sophisticated%20tech%20scene&width=1200&height=600&seq=art-hero-v2-06&orientation=landscape",
  "hipobuy-spreadsheet-entreprises": "https://readdy.ai/api/search-image?query=Diverse%20team%20of%20professionals%20in%20glass%20meeting%20room%20discussing%20large%20screen%20spreadsheet%20data%2C%20corporate%20collaboration%2C%20bright%20natural%20light%2C%20modern%20office%20photography&width=1200&height=600&seq=art-hero-v2-07&orientation=landscape",
  "automatisation-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Futuristic%20automation%20concept%20with%20digital%20workflow%20diagrams%20floating%20above%20laptop%20on%20clean%20white%20desk%2C%20innovative%20technology%2C%20soft%20glowing%20elements%2C%20modern%20workspace&width=1200&height=600&seq=art-hero-v2-08&orientation=landscape",
  "migration-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Smooth%20data%20transition%20visualized%20with%20flowing%20light%20streams%20between%20old%20and%20new%20laptop%20screens%2C%20migration%20concept%2C%20clean%20modern%20workspace%2C%20hopeful%20atmosphere&width=1200&height=600&seq=art-hero-v2-09&orientation=landscape",
  "securite-donnees-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Digital%20shield%20and%20lock%20icons%20floating%20over%20laptop%20showing%20encrypted%20data%20on%20dark%20background%2C%20cybersecurity%20concept%2C%20professional%20photography%2C%20trust%20and%20protection%20theme&width=1200&height=600&seq=art-hero-v2-10&orientation=landscape",
  "productivite-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Perfectly%20organized%20minimalist%20desk%20with%20productivity%20tools%20planner%20laptop%20and%20healthy%20snack%2C%20morning%20routine%20setup%2C%20golden%20sunlight%2C%20motivational%20workspace%20photography&width=1200&height=600&seq=art-hero-v2-11&orientation=landscape",
  "personnalisation-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Creative%20workspace%20with%20color%20swatches%20customized%20spreadsheet%20template%20on%20screen%20and%20design%20tools%2C%20artistic%20professional%20setup%2C%20bright%20daylight%2C%20personalization%20concept&width=1200&height=600&seq=art-hero-v2-12&orientation=landscape",
  "temoignages-hipobuy-spreadsheet": "https://readdy.ai/api/search-image?query=Collage%20of%20diverse%20professional%20portraits%20with%20speech%20bubbles%20and%20five%20star%20ratings%2C%20customer%20testimonials%20concept%2C%20warm%20inviting%20atmosphere%2C%20social%20proof%20editorial%20style&width=1200&height=600&seq=art-hero-v2-13&orientation=landscape",
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!slug || !articleContents[slug]) {
    return <Navigate to="/" replace />;
  }

  const content = articleContents[slug];
  const heroImg = articleHeroImages[slug];
  const articleUrl = `${siteUrl}/article/${slug}`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${articleUrl}/#article`,
        headline: content.title,
        description: content.intro.replace(/<[^>]*>/g, ""),
        url: articleUrl,
        inLanguage: "fr",
        isPartOf: { "@id": `${siteUrl}/#website` },
        publisher: { "@id": `${siteUrl}/#organization` },
        author: {
          "@type": "Organization",
          name: "Hipobuy.yachts",
          url: siteUrl,
        },
        datePublished: "2026-06-23",
        dateModified: "2026-06-23",
        articleSection: content.category,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": articleUrl,
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${articleUrl}/#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: siteUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Articles",
            item: `${siteUrl}/#articles`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: content.title,
          },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": `${articleUrl}/#faq`,
        mainEntity: content.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a.replace(/<[^>]*>/g, ""),
          },
        })),
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={content.title + " | Hipobuy Spreadsheet"}
        description={content.intro.replace(/<[^>]*>/g, "").substring(0, 160)}
        keywords={`${content.category.toLowerCase()}, hipobuy spreadsheet, guide hipobuy, outil hipobuy`}
        canonicalUrl={articleUrl}
        ogTitle={content.title}
        ogDescription={content.intro.replace(/<[^>]*>/g, "").substring(0, 160)}
        ogUrl={articleUrl}
        ogType="article"
        twitterCard="summary_large_image"
        twitterTitle={content.title}
        twitterDescription={content.intro.replace(/<[^>]*>/g, "").substring(0, 160)}
        jsonLd={articleJsonLd}
        imageAlt={`Hipobuy Spreadsheet - ${content.title}`}
      />
      <Navbar />

      <article className="pt-24 pb-16 md:pb-24 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-foreground-500 mb-8 flex flex-wrap items-center gap-1" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-primary-600 transition-colors">Accueil</Link>
            <span className="mx-1">/</span>
            <Link to="/#articles" className="hover:text-primary-600 transition-colors">Articles</Link>
            <span className="mx-1">/</span>
            <span className="text-foreground-700 line-clamp-1">{content.title}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-md mb-4">
              {content.category}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight">
              {content.title}
            </h1>
            <p className="mt-4 text-foreground-600 text-sm md:text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: content.intro }}></p>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden h-56 md:h-80">
            <img
              src={heroImg}
              alt={content.title}
              title={`Hipobuy Spreadsheet - ${content.title}`}
              className="w-full h-full object-cover"
            />
          </div>

          {content.sections.map((section, sIdx) => (
            <section key={sIdx} className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                {section.heading}
              </h2>
              {section.paragraphs.map((p, pIdx) => (
                <p key={pIdx} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }}></p>
              ))}
              {section.listItems && (
                <ul className="space-y-2 mb-4 pl-1">
                  {section.listItems.map((item, lIdx) => (
                    <li key={lIdx} className="flex items-start gap-3 text-foreground-700 text-sm">
                      <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {section.tableData && (
                <div className="overflow-x-auto mt-4 mb-4">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-background-300">
                        {section.tableData.headers.map((h, hIdx) => (
                          <th key={hIdx} className="text-left py-3 px-4 font-semibold text-foreground-900 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.tableData.rows.map((row, rIdx) => (
                        <tr key={rIdx} className="border-b border-background-200">
                          {row.map((cell, cIdx) => (
                            <td key={cIdx} className="py-3 px-4 text-foreground-700 whitespace-nowrap">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              Questions fréquentes
            </h2>
            <div className="space-y-3">
              {content.faqs.map((faq, fIdx) => (
                <details key={fIdx} className="bg-background-100 rounded-xl group">
                  <summary className="px-5 py-4 cursor-pointer font-heading text-foreground-900 font-semibold text-sm md:text-base flex items-center justify-between">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0 ml-3"></i>
                  </summary>
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.a }}></div>
                </details>
              ))}
            </div>
          </section>

          <aside className="bg-primary-50 border border-primary-100 rounded-2xl p-6 md:p-8 mb-10">
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
              Prêt à optimiser votre workflow ?
            </h3>
            <p className="text-foreground-600 text-sm mb-5">
              Rejoignez des milliers d&rsquo;utilisateurs qui font confiance au <strong>hipobuy spreadsheet</strong> pour gérer leurs données efficacement.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all text-center whitespace-nowrap"
              >
                Accéder à Kaspreadsheet
              </a>
              <Link
                to="/"
                className="px-6 py-3 bg-background-50 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-all text-center border border-background-200 whitespace-nowrap"
              >
                ← Retour à l&rsquo;accueil
              </Link>
            </div>
          </aside>

          <nav className="border-t border-background-200 pt-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-600 transition-colors text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              Retour à la page d&rsquo;accueil
            </Link>
            <Link
              to="/#articles"
              className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-600 transition-colors text-sm"
            >
              Voir tous les articles
              <i className="ri-arrow-right-line"></i>
            </Link>
          </nav>
        </div>
      </article>

      <Footer />
    </>
  );
}